import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Activity as ActivityIcon, Trophy, Phone, Mail, Users as UsersIcon, MessageSquare } from "lucide-react";

const TYPE_ICONS = { Call: Phone, Email: Mail, Meeting: UsersIcon, SMS: MessageSquare, Other: ActivityIcon };

export default function ActivitiesWeekCard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/activities/stats?period=week")
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  if (loading) {
    return (
      <div className="eyb-card p-5" data-testid="activities-week-card">
        <div className="flex items-center gap-2 mb-3">
          <ActivityIcon className="w-4 h-4 text-[#29ABE2]" />
          <h3 className="font-heading font-semibold text-slate-900">Activities this week</h3>
        </div>
        <div className="text-xs text-slate-400">Loading…</div>
      </div>
    );
  }

  if (!stats) return null;

  const maxCount = Math.max(1, ...(stats.leaderboard || []).map((x) => x.count));
  const topCount = stats.leaderboard?.[0]?.count || 0;

  return (
    <div className="eyb-card p-5" data-testid="activities-week-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ActivityIcon className="w-4 h-4 text-[#29ABE2]" />
          <h3 className="font-heading font-semibold text-slate-900">Activities this week</h3>
        </div>
        <span className="text-[11px] text-slate-400">{stats.period_start} → {stats.period_end}</span>
      </div>

      <div className="flex items-baseline gap-4 flex-wrap mb-4">
        <div>
          <div className="eyb-label">{isAdmin ? "Total logged" : "Your activity"}</div>
          <div
            className="font-heading text-3xl font-bold text-slate-900"
            data-testid={isAdmin ? "activities-total" : "activities-my-count"}
          >
            {isAdmin ? stats.total : stats.my_count}
          </div>
        </div>
        {isAdmin && (
          <div>
            <div className="eyb-label">Your contribution</div>
            <div className="font-heading text-xl font-semibold text-slate-700" data-testid="activities-my-count">{stats.my_count}</div>
          </div>
        )}
      </div>

      {/* by_type counts */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {Object.entries(stats.by_type || {}).map(([t, c]) => {
          const Icon = TYPE_ICONS[t] || ActivityIcon;
          return (
            <div key={t} className="flex flex-col items-center px-1.5 py-2 rounded-md bg-slate-50" data-testid={`activities-type-${t.toLowerCase()}`}>
              <Icon className="w-3.5 h-3.5 text-slate-500 mb-1" />
              <div className="text-base font-bold text-slate-900 leading-none">{c}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{t}</div>
            </div>
          );
        })}
      </div>

      {/* leaderboard (admin only) */}
      {isAdmin && (stats.leaderboard || []).length > 0 && (
        <div data-testid="activities-leaderboard">
          <div className="flex items-center gap-1.5 mb-2">
            <Trophy className="w-3.5 h-3.5 text-amber-500" />
            <div className="text-xs font-semibold text-slate-700">Most active rep</div>
          </div>
          <div className="space-y-1.5">
            {stats.leaderboard.slice(0, 5).map((row, i) => {
              const pct = (row.count / maxCount) * 100;
              const isTop = row.count === topCount;
              return (
                <div key={row.user_id} className="flex items-center gap-2" data-testid={`leaderboard-row-${i}`}>
                  <div className="w-32 shrink-0 text-xs text-slate-700 truncate">{row.user_name}</div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${isTop ? "bg-gradient-to-r from-amber-400 to-orange-500" : "bg-[#29ABE2]"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className={`w-8 text-right text-xs font-bold ${isTop ? "text-orange-600" : "text-slate-700"}`}>
                    {row.count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {stats.total === 0 && (
        <p className="text-xs text-slate-500">No activities logged this week yet. Log calls, emails and meetings from the Activity tab on any lead or opportunity.</p>
      )}
    </div>
  );
}
